// --- Dependencies -------------------------------------------------------------------------------
import { InputAdornment, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import "../field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    label: string;
    fromLabel: string;
    toLabel: string;
    from: number | null;
    to: number | null;
    onFrom: (value: number | null) => void;
    onTo: (value: number | null) => void;
    suffix?: string;
    disabled?: boolean;
};

/**
 * Grouped as it is typed. `1 500 000` is a figure somebody can check at a glance; `1500000` is a
 * string of digits they have to count, and a budget is the field where a misplaced zero costs the
 * most. A **space** rather than a comma: it reads the same in both languages and never collides
 * with a decimal separator.
 */
const group = (value: number | null): string => (value === null ? "" : value.toLocaleString("en-US").replace(/,/g, " "));

/** Everything that is not a digit is dropped, which is what makes the spaces safe to type over. */
const ungroup = (value: string): number | null => {
    const digits = value.replace(/[^\d]/g, "");
    return digits === "" ? null : Number(digits);
};

/**
 * Two ends of one number, as one control.
 *
 * A budget is a single question, and asking it as two full-width fields stacked down a form made it
 * read as two unrelated ones — people filled the first and left the second. So they share a caption
 * above and a border below: joined, facing corners squared, the seam drawn once instead of twice,
 * and each end keeping its own floating label so a filled range still says which end is which.
 *
 * The **pair** follows the page and only the **digits** are forced `ltr`. "From" on the right and
 * "to" on the left is the correct Arabic reading order for two labelled boxes; forcing the row
 * itself left-to-right put each floating label at the wrong end of its own box, because the label
 * rules are mirrored off the document direction rather than the field's.
 *
 * It is a **text** field, not a number one: `type="number"` cannot hold a grouped value, and its
 * spinners are two arrows nobody presses on purpose and a scroll wheel that rewrites prices.
 */
export default function RangeInput({ label, fromLabel, toLabel, from, to, onFrom, onTo, suffix, disabled }: Props) {
    const end = (value: number | null, onChange: (next: number | null) => void, endLabel: string, side: "from" | "to") => (
        <TextField
            className={`field range-input__end range-input__end--${side}`}
            value={group(value)}
            onChange={(event) => onChange(ungroup(event.target.value))}
            label={endLabel}
            type="text"
            disabled={disabled}
            slotProps={{
                htmlInput: { dir: "ltr", inputMode: "numeric", autoComplete: "off" },
                input: suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : undefined,
            }}
            variant="outlined"
        />
    );

    return (
        <div className="range-input">
            <span className="range-input__label">{label}</span>

            <div className="range-input__pair">
                {end(from, onFrom, fromLabel, "from")}
                {end(to, onTo, toLabel, "to")}
            </div>
        </div>
    );
}
